import { Collapsible, CollapsibleContent } from "@repo/ui/components/ui/collapsible";
import { createContext, type ReactNode, use } from "react";
import { DetailAccordion } from "@/shared/common/detail-accordion";
import { ChartSectionTrigger } from "./chart-section-trigger";
import { ChartingActions, ChartingStateProvider, useCanChart } from "./charting-state";
import type { ChartingStatus } from "./charting-status-badge";
import { ChartingStatusBadge } from "./charting-status-badge";

// ============================================================
// ChartCard — the frame a treatment or a diagnostic is charted in.
//
// CHARTING, NOT PROCEDURE. The word covers exams too; the card does not,
// because an exam's findings carry prerequisite chains and diagnostic backing
// and are a different structure, not a variant of this one.
//
// WHAT IT OWNS is the chrome the cards kept re-deriving and drifting apart on:
// the accordion frame, the title row, the status badge, and the observation
// section's rhythm. The NOTE is shared through `chartNoteDescriptor` instead —
// a wrapper cannot stop two cards picking different controls, because the
// control is chosen by the descriptor, which is where they actually drifted.
//
// WHAT IT DOES NOT OWN is how observations are rendered, how either card
// batches its saves, or where the edit lock sits. Treatment holds one
// AutosaveSection for the whole card and the diagnostic holds one per
// observation; collapsing that is a change to save semantics, not layout. And
// the lock cannot live here: treatment enables it from config loaded inside the
// body, which the frame has not seen yet. All three arrive as children, so the
// divergence stays visible instead of hiding behind a flag.
// ============================================================

interface ChartCardState {
  /** Drives the badge. Absent or unresolved renders nothing — see ChartingStatusBadge. */
  status?: ChartingStatus;
}

interface ChartCardMeta {
  title: string;
  /** Rendered under the title, e.g. a definition version. */
  subtitle?: ReactNode;
  /** Anchor target so another card can link to this one. */
  anchorId: string;
  /** Accordion identity, stable across renders. */
  sectionValue: string;
}

interface ChartCardContextValue {
  state: ChartCardState;
  meta: ChartCardMeta;
}

export const ChartCardContext = createContext<ChartCardContextValue | null>(null);

function useChartCard(component: string): ChartCardContextValue {
  const value = use(ChartCardContext);
  if (!value) throw new Error(`<ChartCard.${component}> must be rendered inside <ChartCard>`);
  return value;
}

/**
 * `outcome` is a slot, not a variant flag: treatment puts buttons there and the
 * diagnostic puts a chip field, and neither the card nor a `kind` prop needs to
 * know which. It mirrors `DetailAccordion.Section`'s own `actions` prop rather
 * than inventing a second spelling for the same idea.
 */
function Root({
  state,
  meta,
  outcome,
  footer,
  children,
}: {
  state: ChartCardState;
  meta: ChartCardMeta;
  outcome?: ReactNode;
  /** Stays visible when the card is collapsed — where the note goes. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <ChartCardContext value={{ state, meta }}>
      <DetailAccordion.Section
        id={meta.anchorId}
        value={meta.sectionValue}
        title={<Title />}
        actions={<ChartingActions>{outcome}</ChartingActions>}
        footer={footer}
        keepMounted
      >
        {/* No padding of its own: the accordion panel already insets the card
            top and base, and stacking a second bottom inset left a dead strip
            under the note. */}
        <div className="space-y-4">{children}</div>
      </DetailAccordion.Section>
    </ChartCardContext>
  );
}

function Title() {
  const { state, meta } = useChartCard("Title");
  return (
    <div>
      <span className="flex items-center gap-2">
        <span>{meta.title}</span>
        <ChartingStatusBadge status={state.status} />
      </span>
      {meta.subtitle && <p className="text-xs font-normal text-muted-foreground">{meta.subtitle}</p>}
    </div>
  );
}

/**
 * SPACING, AND THE ONE GATE THAT DIFFERS. It does not decide which control the
 * note gets — that is `chartNoteDescriptor`'s job, and a wrapper claiming
 * otherwise is how the two cards drifted into an editor on one and a textarea
 * on the other. The padding exists because the inline note strips its own to
 * read as an annotation, which leaves it flush against the card edge while
 * every other row is inset.
 *
 * A NOTE IS NOT A CLINICAL VALUE, so an outcome does not close it — writing up
 * what happened after marking the work complete is the ordinary case, and the
 * domain has always allowed it. It therefore renders OUTSIDE
 * `ChartingStateFieldset`: a disabled fieldset disables every descendant and a
 * nested one cannot opt back in. The explicit note scope resets only the
 * record's outcome restriction; a booked or finished visit stays read-only.
 */
function Note({ children }: { children: ReactNode }) {
  useChartCard("Note");
  return (
    <ChartingStateProvider purpose="note">
      <NoteFields>{children}</NoteFields>
    </ChartingStateProvider>
  );
}

function NoteFields({ children }: { children: ReactNode }) {
  const editable = useCanChart();
  return (
    <fieldset disabled={!editable} className={editable ? "px-3.5 pt-3" : "px-3.5 pt-3 opacity-50"}>
      {children}
    </fieldset>
  );
}

/**
 * The observations region, collapsible under its own header like the trials
 * above it. The header counts what is captured so a collapsed card still says
 * what is left; an observation marked unable to capture is not captured.
 */
function Observations({
  title = "Observations",
  captured,
  total,
  children,
}: {
  title?: string;
  captured: number;
  total?: number;
  children: ReactNode;
}) {
  useChartCard("Observations");
  return (
    <Collapsible render={<section />} className="space-y-2">
      <ChartSectionTrigger>
        {title}
        <span className="text-xs font-normal text-muted-foreground">
          {total === undefined
            ? `${captured} ${captured === 1 ? "reading" : "readings"}`
            : `${captured} of ${total} captured`}
        </span>
      </ChartSectionTrigger>
      <CollapsibleContent className="space-y-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

/**
 * One named observation and its fields. The label and the per-observation
 * actions sit on one row so a treatment and a diagnostic cannot disagree about
 * where either goes — which is exactly how the diagnostic ended up boxing its
 * groups in a tint the treatment never had.
 */
function ObservationGroup({
  label,
  actions,
  uncaptured,
  children,
}: {
  label: string;
  actions?: ReactNode;
  /** Collapses the group to its title row, hiding the fields. */
  uncaptured?: boolean;
  children: ReactNode;
}) {
  useChartCard("ObservationGroup");
  return (
    // THE OBSERVATION IS THE UNIT, not the field. The fill used to sit on each
    // field, so a two-component reading was two boxes under a loose heading and
    // a one-component one was a box whose heading floated outside it. The
    // container moved up here and `ChartCell` stands down inside it.
    <ObservationGroupContext value={true}>
      {/* The exam finding's container, class for class — an observation and a
          finding are the same kind of thing on the page, and the only reason
          they looked different was that nobody had said so. */}
      <div className="space-y-3 rounded-lg bg-muted/40 p-3.5 text-sm has-data-[invalid]:bg-destructive/5">
        {/* Actions sit NEXT TO the title, not pushed to the far edge — the exam
            finding's own header shape. Spread across the card they read as
            belonging to the row rather than to the observation they act on. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-semibold leading-snug">{label}</span>
          <ChartingActions>{actions}</ChartingActions>
        </div>
        {/* Collapsed to the title row. The selected pill beside the name
            already says it is uncaptured, so a line repeating the pill's own
            words was the same fact twice. */}
        {uncaptured ? null : children}
      </div>
    </ObservationGroupContext>
  );
}

/**
 * Whether a field is already inside an observation's container.
 *
 * Read by `ChartCell`, which supplies its own fill and padding everywhere else
 * — a questionnaire item, an exam finding, a bare parameter. Nesting the two
 * double-tinted every charted observation, so the cell defers to the group.
 */
const ObservationGroupContext = createContext(false);

export function useInObservationGroup(): boolean {
  return use(ObservationGroupContext);
}

/**
 * The members of one multi-component observation.
 *
 * Stacked, every component was a full-width tinted cell, so a two-part reading
 * — systolic over diastolic — read as two unrelated observations under a
 * heading. `grid` pairs them; `stack` is for the ones a half-width cell would
 * crush. Which one applies is `componentLayout`'s call, not the card's.
 */
function Components({ layout, children }: { layout: "grid" | "stack"; children: ReactNode }) {
  useChartCard("Components");
  // `items-start` so a wrapping label does not stretch its neighbour's fill.
  return <div className={layout === "grid" ? "grid grid-cols-2 items-start gap-2" : "space-y-2"}>{children}</div>;
}

export const ChartCard = Object.assign(Root, { Title, Note, Observations, ObservationGroup, Components });
export type { ChartCardMeta, ChartCardState };
